import { Button } from '@oysterjs/ui/button';
import { DialogActions, DialogBody, DialogTitle, MountableDialog } from '@oysterjs/ui/dialog';
import { Spinner } from '@oysterjs/ui/spinner';

import { InlineNotice } from '~/components/inline-notice';
import { rowDateTimeFormatter } from '~/components/table-formatters';

/**
 * The sent email a push is about, as the confirm describes it. sentAt arrives as an ISO
 * string, though the Time scalar is typed as a Date.
 */
export type PushedEmailSummary = {
  subject: string;
  recipients: string;
  sentAt: Date | string | null;
};

/** Where the confirm stands: before the push, during it, or how it ended without logging. */
export type PushConfirmState =
  | { kind: 'ready' }
  | { kind: 'logging' }
  /** Nothing was added, so trying again is safe. */
  | { kind: 'failed'; reason: string | null }
  | { kind: 'signedOut' }
  | { kind: 'unconfirmed' }
  /** Cara refused before writing anything, for the reason given. */
  | { kind: 'refused'; message: string }
  /** The answer never arrived, though the server finishes the write. */
  | { kind: 'connectionLost' };

/** Where looking up the email's client stands, before the confirm can name one. */
export type PushLookupState =
  | { kind: 'finding' }
  /** Cara couldn't be reached, so looking again may work. */
  | { kind: 'unreachable' }
  /** Cara answered that it won't look, for the reason given. */
  | { kind: 'refused'; message: string };

const pushDialogTitle = (amsName: string, isLogAgain: boolean) =>
  isLogAgain ? 'Log this email again?' : `Log this email to ${amsName}?`;

const EmailSummaryCard = ({ email }: { email: PushedEmailSummary }) => (
  <div className="rounded-lg bg-neutral-50 dark:bg-neutral-900 px-3 py-2.5 ring-1 ring-neutral-200 dark:ring-neutral-700">
    <span className="block truncate text-sm font-medium text-neutral-900 dark:text-white">
      {email.subject}
    </span>
    <span className="block break-words text-xs text-neutral-500 dark:text-neutral-400">
      To {email.recipients}
      {email.sentAt && ` · Sent ${rowDateTimeFormatter.format(new Date(email.sentAt))}`}
    </span>
  </div>
);

const ResultNotice = (props: { state: PushConfirmState; amsName: string; clientName: string }) => {
  const { state, amsName } = props;
  switch (state.kind) {
    case 'failed':
      return (
        <InlineNotice
          tone="error"
          title={`Couldn't log to ${amsName}.`}
          description={
            state.reason ??
            `${amsName} didn't respond. Nothing was added. Try again in a few minutes.`
          }
        />
      );
    case 'signedOut':
      return (
        <InlineNotice
          tone="error"
          title={`${amsName} signed Cara out.`}
          description={`Ask an admin to reconnect ${amsName} in Settings > Integrations, then try again.`}
        />
      );
    case 'unconfirmed':
      return (
        <InlineNotice
          tone="warning"
          title="Couldn't confirm it was logged."
          description={`${amsName} didn't answer in time, so the email may already be on ${props.clientName}'s record. Check ${amsName} before trying again.`}
        />
      );
    case 'refused':
      return (
        <InlineNotice
          tone="error"
          title={`Couldn't log to ${amsName}.`}
          description={state.message}
        />
      );
    case 'connectionLost':
      return (
        <InlineNotice
          tone="warning"
          title="Lost the connection to Cara."
          description="Cara may still be logging it. This email's AMS status updates in a moment."
        />
      );
    case 'ready':
    case 'logging':
      return null;
  }
};

/**
 * Asks before logging a sent email to the AMS, since a logged email can't be removed
 * from Cara. isLogAgain leads with the warning that the first attempt may have landed.
 * Mount it inside an AnimatePresence.
 */
export const PushConfirmDialog = (props: {
  amsName: string;
  clientName: string;
  email: PushedEmailSummary;
  isLogAgain: boolean;
  state: PushConfirmState;
  onConfirm: () => void;
  onChangeFilingLocation?: () => void;
  onClose: () => void;
  contentOnly?: boolean;
}) => {
  const { amsName, clientName, state } = props;
  const isLogging = state.kind === 'logging';
  const canConfirm = state.kind === 'ready' || state.kind === 'failed' || isLogging;
  const confirmLabel = isLogging
    ? 'Logging…'
    : state.kind === 'failed'
      ? 'Try again'
      : props.isLogAgain
        ? 'Log it again'
        : 'Log it';
  // Closing mid-push would hide how it ended.
  const close = isLogging ? () => {} : props.onClose;

  const content = (
    <>
      <DialogTitle>{pushDialogTitle(amsName, props.isLogAgain)}</DialogTitle>
      <DialogBody className="space-y-4 text-sm/6 text-neutral-600 dark:text-neutral-300">
        {props.isLogAgain ? (
          <InlineNotice
            tone="warning"
            title="Cara couldn't confirm the first attempt."
            description={
              <>
                If it did land, this adds a second copy to <strong>{clientName}</strong>&apos;s
                record in {amsName}. It can&apos;t be undone from Cara.
              </>
            }
          />
        ) : (
          <div>
            This adds the email to{' '}
            <strong className="text-neutral-900 dark:text-white">{clientName}</strong>&apos;s record
            in {amsName}. It can&apos;t be undone from Cara.
          </div>
        )}
        <EmailSummaryCard email={props.email} />
        <ResultNotice state={state} amsName={amsName} clientName={clientName} />
      </DialogBody>
      <DialogActions>
        <Button color="white" onClick={props.onClose} disabled={isLogging}>
          {canConfirm ? 'Cancel' : 'Close'}
        </Button>
        {canConfirm && props.onChangeFilingLocation && (
          <Button color="white" disabled={isLogging} onClick={props.onChangeFilingLocation}>
            Change filing location
          </Button>
        )}
        {canConfirm && (
          <Button loading={isLogging} onClick={props.onConfirm}>
            {confirmLabel}
          </Button>
        )}
      </DialogActions>
    </>
  );
  return props.contentOnly ? (
    content
  ) : (
    <MountableDialog onClose={close} size="md">
      {content}
    </MountableDialog>
  );
};

const LookupNotice = ({ lookup }: { lookup: PushLookupState }) => {
  switch (lookup.kind) {
    case 'finding':
      return null;
    case 'unreachable':
      return (
        <InlineNotice
          tone="error"
          title="Couldn't look up this email's client."
          description="Check your connection and try again."
        />
      );
    case 'refused':
      return (
        <InlineNotice
          tone="error"
          title="Couldn't look up this email's client."
          description={lookup.message}
        />
      );
  }
};

/**
 * The push dialog while Cara looks up which client the email's recipients match, before
 * it can name one. Mount it inside an AnimatePresence.
 */
export const PushFindingClientDialog = (props: {
  amsName: string;
  isLogAgain: boolean;
  lookup: PushLookupState;
  onRetry: () => void;
  onClose: () => void;
  contentOnly?: boolean;
}) => {
  const { lookup } = props;
  const content = (
    <>
      <DialogTitle>{pushDialogTitle(props.amsName, props.isLogAgain)}</DialogTitle>
      <DialogBody className="text-sm/6 text-neutral-600 dark:text-neutral-300">
        {lookup.kind === 'finding' ? (
          <div role="status" className="flex items-center gap-2">
            <Spinner size="size-4" color="neutral" />
            Looking for this email&apos;s client in {props.amsName}…
          </div>
        ) : (
          <LookupNotice lookup={lookup} />
        )}
      </DialogBody>
      <DialogActions>
        <Button color="white" onClick={props.onClose}>
          {lookup.kind === 'refused' ? 'Close' : 'Cancel'}
        </Button>
        {lookup.kind === 'unreachable' && <Button onClick={props.onRetry}>Try again</Button>}
      </DialogActions>
    </>
  );
  return props.contentOnly ? (
    content
  ) : (
    <MountableDialog onClose={props.onClose} size="md">
      {content}
    </MountableDialog>
  );
};
